import React, { useEffect, useMemo, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { getAllSettings, updateSettings } from "../../store/actions/settings";
import { useDispatch, useSelector } from "react-redux";
import { Switch } from "antd";

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    ["blockquote", "code-block"],
    [{ color: [] }, { background: [] }],
    ["link"],
    ["clean"],
  ],
};

const QUILL_FORMATS = [
  "header",
  "bold", "italic", "underline", "strike",
  "list", "bullet", "indent",
  "blockquote", "code-block",
  "color", "background",
  "link",
];

const wordCount = (html = "") => {
  const text = html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  return text ? text.split(" ").length : 0;
};

const charCount = (html = "") =>
  html.replace(/<[^>]*>/g, "").replace(/\s+/g, "").length;

const isEmpty = (html = "") =>
  !html || html === "<p><br></p>" || html.trim() === "";

const POLICY_QUILL_STYLE = (id, primary) => `
  .pq-${id} .ql-toolbar {
    border: none !important;
    border-bottom: 1px solid #e2e8f0 !important;
    padding: 8px 14px !important;
    background: #f8fafc !important;
    flex-wrap: wrap;
  }
  .pq-${id} .ql-container {
    border: none !important;
    font-size: 14px !important;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
  }
  .pq-${id} .ql-editor {
    min-height: 220px !important;
    padding: 18px 22px !important;
    line-height: 1.75 !important;
    color: #1e293b !important;
  }
  .pq-${id} .ql-editor.ql-blank::before {
    color: #94a3b8 !important;
    font-style: normal !important;
    font-size: 14px !important;
    left: 22px !important;
  }
  .pq-${id} .ql-toolbar button:hover .ql-stroke,
  .pq-${id} .ql-toolbar button.ql-active .ql-stroke { stroke: ${primary} !important; }
  .pq-${id} .ql-toolbar button:hover .ql-fill,
  .pq-${id} .ql-toolbar button.ql-active .ql-fill  { fill:   ${primary} !important; }
  .pq-${id} .ql-toolbar button:hover,
  .pq-${id} .ql-toolbar button.ql-active { color: ${primary} !important; }
  .pq-${id} .ql-toolbar .ql-stroke { stroke: #64748b !important; }
  .pq-${id} .ql-toolbar .ql-fill  { fill:   #64748b !important; }
  .pq-${id} .ql-toolbar .ql-picker-label { color: #64748b !important; }
`;

const PolicyCard = ({
  id,
  icon,
  title,
  description,
  placeholder,
  value,
  onChange,
  onSave,
  primaryColor = "#6366f1",
}) => {
  const [focused, setFocused] = useState(false);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved

  const handleSave = async () => {
    setSaveState("saving");
    await onSave();
    setSaveState("saved");
    setTimeout(() => setSaveState("idle"), 2400);
  };

  const words = wordCount(value);
  const chars = charCount(value);
  const hasContent = !isEmpty(value);

  const saveBtnBg =
    saveState === "saved"
      ? "#10b981"
      : saveState === "saving"
      ? "#a5b4fc"
      : primaryColor;

  return (
    <>
      <style>{POLICY_QUILL_STYLE(id, primaryColor)}</style>
      <div
        style={{
          background: "#ffffff",
          border: `1.5px solid ${focused ? primaryColor : "#e2e8f0"}`,
          borderRadius: "16px",
          overflow: "hidden",
          marginBottom: "50px",
          transition: "border-color 0.2s, box-shadow 0.2s",
          boxShadow: focused
            ? `0 0 0 4px ${primaryColor}1a`
            : "0 1px 3px rgba(0,0,0,0.06)",
        }}
      >
        {/* ── Header ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 20px",
            background: "#f8fafc",
            borderBottom: "1px solid #e2e8f0",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          {/* Left: icon + labels */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "10px",
                background: `${primaryColor}18`,
                color: primaryColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {icon}
            </div>
            <div>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#1e293b",
                  letterSpacing: "-0.01em",
                }}
              >
                {title}
              </div>
              <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                {description}
              </div>
            </div>
          </div>

          {/* Right: word count + save */}
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            {hasContent && (
              <div
                style={{
                  fontSize: "11.5px",
                  color: "#94a3b8",
                  display: "flex",
                  gap: "8px",
                }}
              >
                <span>{words} words</span>
                <span style={{ opacity: 0.4 }}>·</span>
                <span>{chars} chars</span>
              </div>
            )}
            <button
              onClick={handleSave}
              disabled={saveState === "saving"}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "7px 16px",
                borderRadius: "8px",
                border: "none",
                fontSize: "13px",
                fontWeight: "500",
                cursor: saveState === "saving" ? "not-allowed" : "pointer",
                background: saveBtnBg,
                color: "#fff",
                transition: "background 0.22s, transform 0.1s",
                transform: saveState === "saving" ? "scale(0.97)" : "scale(1)",
                minWidth: "88px",
                justifyContent: "center",
              }}
            >
              {saveState === "saved" ? (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Saved
                </>
              ) : saveState === "saving" ? (
                "Saving…"
              ) : (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                    <polyline points="17 21 17 13 7 13 7 21" />
                    <polyline points="7 3 7 8 15 8" />
                  </svg>
                  Save
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── Quill Editor ── */}
        <div
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        >
          <div className={`pq-${id}`}>
            <ReactQuill
              value={value}
              onChange={onChange}
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              placeholder={placeholder}
              theme="snow"
            />
          </div>
        </div>

        {/* ── Empty state hint ── */}
        {!hasContent && (
          <div
            style={{
              padding: "10px 22px",
              borderTop: "1px dashed #e2e8f0",
              fontSize: "12px",
              color: "#94a3b8",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            Click above to start writing your {title.toLowerCase()}.
          </div>
        )}
      </div>
    </>
  );
};
const IconDoc = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

const IconShieldCheck = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);

const IconRefund = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="1 4 1 10 7 10" />
    <path d="M3.51 15a9 9 0 1 0 .49-3.5" />
  </svg>
);

const LegalSectionHeader = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "20px",
    }}
  >
    <div>
      <h2
        style={{
          fontSize: "20px",
          fontWeight: "700",
          color: "#1e293b",
          margin: 0,
          letterSpacing: "-0.02em",
        }}
      >
        Legal Documents
      </h2>
      <p style={{ fontSize: "13.5px", color: "#64748b", margin: "4px 0 0" }}>
        Manage your store's legal policies — each section saves independently.
      </p>
    </div>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        padding: "6px 13px",
        background: "#ede9fe",
        borderRadius: "8px",
        fontSize: "12px",
        fontWeight: "500",
        color: "#6366f1",
      }}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
      3 documents
    </div>
  </div>
);

const ModernSettingsPage = () => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    email: "",
    mobile: "",
    emailPassKey: "",
    renewReferral: "",
    ewayRatePerPcs: 0,
    eInvoiceRatePerPcs: 0,
    paymentAmount: 0,
    terms: "",
    policy: "",
    refundPolicy: "",
    address: "",
    wpAuthToken: "",
    compName: "",
    gstNo: "",
    gstPer: 0,
    sac: "",
    sendBuddyAuthToken: "",
  });

  const [errors, setErrors] = useState({});

  const validateField = (field, value) => {
    let error = "";
    if (field === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value && !emailRegex.test(value)) {
        error = "Please enter a valid email address";
      }
    } else if (field === "mobile") {
      const mobileRegex = /^\d{10}$/;
      if (value && !mobileRegex.test(value)) {
        error = "Please enter a valid 10-digit mobile number";
      }
    } else if (field === "gstNo") {
      const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
      if (value && !gstRegex.test(value)) {
        error = "Please enter a valid GST number (e.g., 22AAAAA0000A1Z5)";
      }
    }
    setErrors((prev) => ({ ...prev, [field]: error }));
    return error;
  };

  const settings = useSelector((state) => state.settings.list);

  const setting = useMemo(() => {
    return settings[0];
  }, [settings]);

  const [activeTab, setActiveTab] = useState("basic");

  const handleInputChange = (field, value) => {
    let normalizedValue = value;
    if (field === "gstNo" && value) {
      normalizedValue = value.toUpperCase();
    }
    setFormData((prev) => ({
      ...prev,
      [field]: normalizedValue,
    }));
    validateField(field, normalizedValue);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  // Color scheme
  const colors = {
    primary: "#6366f1",
    primaryLight: "#a5b4fc",
    secondary: "#f1f5f9",
    accent: "#10b981",
    danger: "#ef4444",
    text: "#1e293b",
    textLight: "#64748b",
    background: "#f8fafc",
    white: "#ffffff",
    border: "#e2e8f0",
  };

  // Styles
  const containerStyle = {
    minHeight: "100vh",
    backgroundColor: colors.background,
    padding: "24px",
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  };

  const headerStyle = {
    color: colors.text,
    fontSize: "32px",
    fontWeight: "700",
    marginBottom: "8px",
    marginTop: "100px",
    letterSpacing: "-0.025em",
  };

  const subtitleStyle = {
    color: colors.textLight,
    fontSize: "16px",
    marginBottom: "32px",
  };

  const cardStyle = {
    backgroundColor: colors.white,
    borderRadius: "16px",
    padding: "32px",
    marginBottom: "24px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    border: `1px solid ${colors.border}`,
  };

  const cardHeaderStyle = {
    fontSize: "24px",
    fontWeight: "600",
    color: colors.text,
    marginBottom: "24px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
  };

  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "24px",
    marginBottom: "24px",
  };

  const inputGroupStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  };

  const inputGroupStyle2 = {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    gridColumn: "span 2",
  };

  const inputGroupStyle3 = {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    gridColumn: "span 4",
  };

  const labelStyle = {
    fontSize: "14px",
    fontWeight: "500",
    color: colors.text,
    marginBottom: "4px",
  };

  const inputStyle = {
    padding: "12px 16px",
    border: `2px solid ${colors.border}`,
    borderRadius: "8px",
    fontSize: "14px",
    backgroundColor: colors.white,
    color: colors.text,
    transition: "all 0.2s ease",
    outline: "none",
    width: "100%",
  };

  const errorStyle = {
    color: colors.danger,
    fontSize: "12px",
    marginTop: "4px",
    fontWeight: "500",
  };

  const buttonStyle = (variant = "primary") => ({
    padding: "12px 32px",
    borderRadius: "8px",
    border: "none",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s ease",
    backgroundColor: variant === "primary" ? colors.primary : colors.accent,
    color: colors.white,
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  });

  const textareaStyle = {
    ...inputStyle,
    minHeight: "120px",
    resize: "vertical",
    fontFamily: "inherit",
  };

  const IconSettings = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1" />
    </svg>
  );

  const IconShield = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );

  const renderInput = (label, name, type = "text", placeholder = "") => (
    <div style={inputGroupStyle}>
      <label style={labelStyle}>{label}</label>
      <input
        type={type}
        value={formData[name]}
        onChange={(e) => handleInputChange(name, e.target.value)}
        placeholder={placeholder}
        style={{
          ...inputStyle,
          borderColor: errors[name] ? colors.danger : colors.border,
        }}
        onFocus={(e) =>
          (e.target.style.borderColor = errors[name]
            ? colors.danger
            : colors.primary)
        }
        onBlur={(e) =>
          (e.target.style.borderColor = errors[name]
            ? colors.danger
            : colors.border)
        }
      />
      {errors[name] && <div style={errorStyle}>{errors[name]}</div>}
    </div>
  );

  const onChange = (checked) => {
    setFormData((prev) => ({ ...prev, renewReferral: checked }));
  };

  const onSubmit = async () => {
    // Validate all fields before submission
    const emailError = validateField("email", formData.email);
    const mobileError = validateField("mobile", formData.mobile);
    const gstError = validateField("gstNo", formData.gstNo);

    if (emailError || mobileError || gstError) {
      alert("Please fix the validation errors before submitting.");
      return;
    }

    await dispatch(
      updateSettings({
        ...formData,
        sendBuddyAuthToken: formData.sendBuddyAuthToken,
        eInvoiceRatePerPcs: Number(formData.eInvoiceRatePerPcs || 0),
        ewayRatePerPcs: Number(formData.ewayRatePerPcs || 0),
        paymentAmount: Number(formData.paymentAmount || 0),
        gstPer: Number(formData.gstPer || 0),
        mobile: Number(formData.mobile || 0),
        id: setting?.id,
      })
    );
  };

  useEffect(() => {
    dispatch(getAllSettings());
  }, [dispatch]);

  useEffect(() => {
    if (setting) {
      setFormData({
        email: setting?.email,
        mobile: setting?.mobile,
        emailPassKey: setting?.emailPassKey,
        renewReferral: setting?.renewReferral,
        ewayRatePerPcs: setting?.ewayRatePerPcs,
        eInvoiceRatePerPcs: setting?.eInvoiceRatePerPcs,
        paymentAmount: setting?.paymentAmount,
        terms: setting?.terms,
        policy: setting?.policy,
        refundPolicy: setting?.refundPolicy,
        wpAuthToken: setting?.wpAuthToken,
        address: setting?.address,
        compName: setting?.compName,
        gstNo: setting?.gstNo,
        gstPer: setting?.gstPer,
        sac: setting?.sac,
        sendBuddyAuthToken: setting?.sendBuddyAuthToken,
      });
    }
  }, [setting]);

  return (
    <div style={containerStyle}>
      <div style={{ maxWidth: "1500px", margin: "5px 5px 5px 5px" }}>
        <div style={headerStyle}>Settings</div>
        <div style={subtitleStyle}>
          Manage your store configuration and preferences
        </div>

        <div>
          {/* ── Business Information ── */}
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>
              <IconSettings />
              Business Information
            </div>
            <div style={gridStyle}>
              {renderInput("Email", "email", "text", "Enter email address")}
              {renderInput("Mobile", "mobile", "text", "Enter mobile number")}
              {renderInput("Email Password", "emailPassKey", "text", "Enter Email password")}

              <div style={inputGroupStyle}>
                <label style={labelStyle}>Renew Referral</label>
                <div>
                  <Switch
                    name="renewReferral"
                    checked={!!formData.renewReferral}
                    onChange={onChange}
                  />
                </div>
              </div>
              {renderInput(
                "Eway Rate Per Pcs",
                "ewayRatePerPcs",
                "number",
                "Enter Eway Rate Per Pcs"
              )}
              {renderInput(
                "Einvoice Rate Per Pcs",
                "eInvoiceRatePerPcs",
                "text",
                "Enter Einvoice Rate Per pcs"
              )}
              {renderInput(
                "Payment Amount",
                "paymentAmount",
                "number",
                "Enter Payment Amount"
              )}
              {renderInput(
                "Company Name",
                "compName",
                "text",
                "Enter Company Name"
              )}
              {renderInput(
                "GST No",
                "gstNo",
                "text",
                "Enter GST No"
              )}
              {renderInput(
                "GST %",
                "gstPer",
                "number",
                "Enter GST %"
              )}
              {renderInput(
                "SAC Code",
                "sac",
                "text",
                "Enter SAC Code"
              )}
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Wp Auth Token</label>
                <input
                  type="text"
                  value={formData?.wpAuthToken}
                  onChange={(e) => handleInputChange("wpAuthToken", e.target.value)}
                  placeholder="Enter Wp Auth Token"
                  style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = colors.primary)}
                  onBlur={(e) => (e.target.style.borderColor = colors.border)}
                />
              </div>

              <div style={inputGroupStyle}>
                <label style={labelStyle}>SendBuddy Auth Token</label>
                <input
                  type="text"
                  value={formData?.sendBuddyAuthToken}
                  onChange={(e) => handleInputChange('sendBuddyAuthToken', e.target.value)}
                  placeholder="Enter SendBuddy Auth Token"
                  style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = colors.primary)}
                  onBlur={(e) => (e.target.style.borderColor = colors.border)}
                />
              </div>

              <div style={inputGroupStyle3}>
                <label style={labelStyle}>Address</label>
                <textarea
                  value={formData?.address}
                  onChange={(e) => handleInputChange("address", e.target.value)}
                  placeholder="Enter Address"
                  style={textareaStyle}
                  onFocus={(e) => (e.target.style.borderColor = colors.primary)}
                  onBlur={(e) => (e.target.style.borderColor = colors.border)}
                />
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "24px" }}>
              <button type="submit" style={buttonStyle()} onClick={() => onSubmit()}>
                Update Business Info
              </button>
            </div>
          </div>

          <LegalSectionHeader />

          {/* Terms & Conditions */}
          <PolicyCard
            id="terms"
            icon={<IconDoc />}
            title="Terms & Conditions"
            description="Legal agreement between your business and users"
            placeholder="Define your terms of service, user responsibilities, and usage agreements..."
            value={formData.terms}
            onChange={(content) =>
              setFormData((prev) => ({ ...prev, terms: content }))
            }
            onSave={onSubmit}
            primaryColor="#6366f1"
          />

          {/* Privacy Policy */}
          <PolicyCard
            id="policy"
            icon={<IconShieldCheck />}
            title="Privacy Policy"
            description="How you collect, use, and protect customer data"
            placeholder="Describe how you collect, use, and protect user data and privacy..."
            value={formData.policy}
            onChange={(content) =>
              setFormData((prev) => ({ ...prev, policy: content }))
            }
            onSave={onSubmit}
            primaryColor="#0ea5e9"
          />

          {/* Refund Policy */}
          <PolicyCard
            id="refund"
            icon={<IconRefund />}
            title="Refund Policy"
            description="Return and refund procedures for customers"
            placeholder="Explain your return, refund, and exchange policies and procedures..."
            value={formData.refundPolicy}
            onChange={(content) =>
              setFormData((prev) => ({ ...prev, refundPolicy: content }))
            }
            onSave={onSubmit}
            primaryColor="#10b981"
          />

          {/* ── Legacy "legal" tab content kept intact ── */}
          {activeTab === "legal" && (
            <>
              {/* Terms and Conditions */}
              <div style={cardStyle}>
                <div style={cardHeaderStyle}>
                  <IconShield />
                  Terms and Conditions
                </div>
                <div style={inputGroupStyle}>
                  <label style={labelStyle}>Terms and Conditions</label>
                  <ReactQuill
                    value={formData.termsCond}
                    onChange={(content) => handleInputChange("termsCond", content)}
                    placeholder="Enter your terms and conditions here..."
                  />
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "24px" }}>
                  <button type="submit" style={buttonStyle()}>
                    Update Terms & Conditions
                  </button>
                </div>
              </div>

              {/* Privacy Policy */}
              <div style={cardStyle}>
                <div style={cardHeaderStyle}>
                  <IconShield />
                  Privacy Policy
                </div>
                <div style={inputGroupStyle}>
                  <label style={labelStyle}>Privacy Policy</label>
                  <ReactQuill
                    value={formData.privacyPolicy}
                    onChange={(content) => handleInputChange("privacyPolicy", content)}
                    placeholder="Enter your privacy policy here..."
                  />
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "24px" }}>
                  <button type="submit" style={buttonStyle()}>
                    Update Privacy Policy
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModernSettingsPage;